import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = useMemo(() => {
    let filtered = articles.filter((a) => a.category === activeCategory);
    if (query) {
      const lower = query.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title.toLowerCase().includes(lower) ||
          a.excerpt.toLowerCase().includes(lower) ||
          a.tags.some((tag) => tag.toLowerCase().includes(lower))
      );
    }
    return filtered;
  }, [activeCategory, query]);

  const categoryClipped = categories.slice(0, 8);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/xuexi-jiaoyu' },
    { label: currentCategory.name },
  ];

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 狠狠撸资源分类`,
    description: currentCategory.description,
    url: `https://henhenlu.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '狠狠撸',
      url: 'https://henhenlu.com/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://henhenlu.com/' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://henhenlu.com/category/xuexi-jiaoyu' },
      { '@type': 'ListItem', position: 3, name: currentCategory.name },
    ],
  };

  return (
    <>
      <SchemaOrg data={collectionSchema} />
      <SchemaOrg data={breadcrumbSchema} />

      <Breadcrumb items={breadcrumbItems} />

      <main className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="w-full lg:w-56 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4">全部分类</h3>
              <div className="flex lg:flex-col gap-1.5 flex-wrap">
                {categoryClipped.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                      activeCategory === cat.slug ? 'bg-primary-100' : 'bg-secondary-100'
                    }`}>
                      <i className={`${cat.icon} text-sm ${activeCategory === cat.slug ? 'text-primary-600' : 'text-secondary-600'}`}></i>
                    </div>
                    <span className="hidden lg:inline">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-xl">
                  <i className={`${currentCategory.icon} text-xl`}></i>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500 mb-1">{currentCategory.description}</p>
              <p className="text-xs text-foreground-400">
                共 {currentCategory.articleCount.toLocaleString()} 条资源
                {query && ` · 搜索 "${query}" 找到 ${categoryArticles.length} 条结果`}
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {categoryArticles.length > 0 ? (
                categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-5 p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="w-full sm:w-48 h-36 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.publishDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.viewCount.toLocaleString()} 阅读
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
                    <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">没有找到相关资源，试试其他分类吧</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}